import React, { useEffect } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { InputParameterConfigForm } from '../InputParameterConfigForm'
import { YesNoCancelModal } from '../../Modal/YesNoCancelModal'
import { Button } from '../../Button/Button'
import { PresetConfigLoader } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Common/PresetConfigLoader'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../ScopedBlock/models'
import { useMeetingTemplateEffectiveInputParameters, useInputParametersUpdateScope, useUserInputParameterConfigForm } from '../hooks'
import { MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../model'

export const MeetingTemplateCurrentUserInputParameterConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  type,
  typeId,
  inputParametersSchema,
  defaultInputParameters,
  meetingTemplateId,
  showCustomizationSetting,
  onHide,
  ipHierarchyEnabled,
  lockedByEnterprise,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    type,
    typeId,
    defaultInputParameters,
    currentUserId,
    meetingTemplateId,
  )

  if (!inputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <CurrentUserConfigFormBody
      inputParametersSchema={inputParametersSchema}
      inputParameters={inputParameters}
      parentInputParameters={parentInputParameters}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      ipHierarchyEnabled={ipHierarchyEnabled}
      lockedByEnterprise={lockedByEnterprise}
    />
  )
}

interface CurrentUserConfigFormBodyProps {
  inputParametersSchema: { [key: string]: any } | undefined
  parentInputParameters: { [key: string]: any }
  inputParameters: { [key: string]: any }
  isCustomized: boolean
  showCustomizationSetting: boolean
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>
  onHide: () => void
  updateLoading: boolean
  ipHierarchyEnabled: boolean
  lockedByEnterprise?: boolean
}

const CurrentUserConfigFormBody: React.FC<CurrentUserConfigFormBodyProps> = ({
  inputParametersSchema,
  parentInputParameters,
  inputParameters,
  isCustomized,
  showCustomizationSetting,
  onSave,
  onHide,
  updateLoading,
  ipHierarchyEnabled,
  lockedByEnterprise = false,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(currentUserId)

  const {
    editedInputParameters,
    customized,
    isChanged,
    showConfirmation,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleClickCancel,
    handleClickSave,
    handleClickClose,
    checkForChange,
  } = useUserInputParameterConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide)

  useEffect(() => {
    checkForChange()
  }, [checkForChange])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters, customized)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <InputParameterConfigForm
        inputParametersSchema={inputParametersSchema}
        inputParameters={editedInputParameters}
        showCustomizationSetting={showCustomizationSetting}
        customized={customized}
        onChange={setEditedInputParameters}
        onChangeCustomized={handleChangeCustomized}
        disabled={!ipHierarchyEnabled || !hasIpHierarchyUpdateScope || lockedByEnterprise}
      />
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickClose}>
          Close
        </Button>
        {hasIpHierarchyUpdateScope && !lockedByEnterprise && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && !isChanged} loading={updateLoading}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
